import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import client, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { NicknameField } from "@/components/wallet/NicknameField";

const KEY = "ol_nick_prompt_skipped";

// Asks existing members without a nickname to pick one; skippable for the current session.
export function NicknamePrompt() {
  const { user, setUser } = useAuth();
  const { pathname } = useLocation();
  const [skipped, setSkipped] = useState(() => sessionStorage.getItem(KEY) === "1");
  const [nick, setNick] = useState("");
  const [valid, setValid] = useState(false);
  const [busy, setBusy] = useState(false);

  const open = !!user?.needs_nickname && !skipped && !pathname.startsWith("/admin") && pathname !== "/complete-profile";
  const skip = () => { sessionStorage.setItem(KEY, "1"); setSkipped(true); };
  const save = async () => {
    setBusy(true);
    try {
      const r = await client.post("/account/nickname", { nickname: nick });
      setUser(r.data.user);
      toast.success(`You're @${r.data.user.nickname}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && skip()}>
      <DialogContent data-testid="nickname-prompt" className="bg-[#0b0b0b] border-white/10 max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display">Choose your OnchainLyfe nickname</DialogTitle>
          <DialogDescription className="text-zinc-400 text-sm">
            Your public handle across OnchainLyfe. Your email and wallets stay private.
          </DialogDescription>
        </DialogHeader>
        <NicknameField value={nick} onChange={setNick} onValidity={setValid} testid="nickname-prompt-input" />
        <div className="flex gap-3 mt-2">
          <button data-testid="nickname-prompt-skip" onClick={skip} className="flex-1 py-3 rounded-full border border-white/15 text-sm text-zinc-300 hover:bg-white/5">Later</button>
          <button data-testid="nickname-prompt-save" disabled={!valid || busy} onClick={save}
            className="flex-1 py-3 rounded-full bg-[#ccff00] text-black font-semibold text-sm disabled:opacity-50">{busy ? "Saving…" : "Save nickname"}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
